// src/components/forms/TeamForm.tsx
import React, { useState, useEffect } from 'react';
import { ITeamMember } from '../../types/Team';
import styles from './TeamForm.module.css';
import { useNavigate } from 'react-router-dom';
import { uploadImage } from '../../services/UploadService';
import { 
  StarIcon,
  EyeIcon,
  EyeSlashIcon,
  PhotoIcon,
  TrashIcon,
  PlusIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

interface TeamFormProps {
    initialData?: ITeamMember;
    onSubmit: (data: ITeamMember, imagesToDelete: string[]) => void;
    isEditing: boolean;
    sessionUploadsRef?: React.MutableRefObject<string[]>;
}

const emptyMember: ITeamMember = { 
    id: Date.now(),
    firstName: '',
    lastName: '',
    email: '',
    position: '',
    bio: '',
    imageUrl: '',
    order: 0,
    isActive: true,
    isFeatured: false
};

const TeamForm: React.FC<TeamFormProps> = ({ initialData, onSubmit, isEditing, sessionUploadsRef }) => {
    const [formData, setFormData] = useState<ITeamMember>(emptyMember);
    const [isUploading, setIsUploading] = useState(false);
    const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState<'basic' | 'profile'>('basic');
    const navigate = useNavigate();

    useEffect(() => {
        if (initialData) {
            const sanitizedData = { ...emptyMember, ...initialData };
            setFormData(sanitizedData);
        } else {
            setFormData(emptyMember);
        }
    }, [initialData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else if (type === 'number') {
            setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || !formData.id) return;
        
        setIsUploading(true);
        const file = files[0];
        try {
            const response = await uploadImage(file, 'team', formData.id);
            setFormData(prev => ({ ...prev, imageUrl: response.url }));
            sessionUploadsRef?.current.push(response.url);
        } catch (error) {
            alert(`Falló la subida de la imagen.`);
        }
        setIsUploading(false);
    };

    const handleRemoveImage = () => {
        if (formData.imageUrl) {
            setImagesToDelete(prev => [...prev, formData.imageUrl]);
        }
        setFormData(prev => ({ ...prev, imageUrl: '' }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData, imagesToDelete);
    };

    return (
        <div className={styles.formContainer}>
            {/* Header con pestañas */}
            <div className={styles.formHeader}>
                <h2 className={styles.formTitle}>
                    {isEditing ? 'Editar Miembro' : 'Agregar Nuevo Miembro'}
                </h2>
                <div className={styles.tabNavigation}>
                    <button
                        type="button"
                        className={`${styles.tabButton} ${activeTab === 'basic' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('basic')}
                    >
                        <InformationCircleIcon className={styles.tabIcon} />
                        Información Básica
                    </button>
                    <button
                        type="button"
                        className={`${styles.tabButton} ${activeTab === 'profile' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('profile')}
                    >
                        <PhotoIcon className={styles.tabIcon} />
                        Foto de Perfil
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Pestaña: Información Básica */}
                {activeTab === 'basic' && (
                    <div className={styles.tabContent}>
                        <div className={styles.formGrid}>
                            {/* Columna 1 */}
                            <div className={styles.formColumn}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="firstName" className={styles.formLabel}>
                                        Nombre *
                                    </label>
                                    <input 
                                        type="text" 
                                        id="firstName" 
                                        name="firstName" 
                                        value={formData.firstName} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: María"
                                        required 
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="lastName" className={styles.formLabel}>
                                        Apellido *
                                    </label>
                                    <input 
                                        type="text" 
                                        id="lastName" 
                                        name="lastName" 
                                        value={formData.lastName} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: García"
                                        required 
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="email" className={styles.formLabel}>
                                        Correo Electrónico
                                    </label>
                                    <input 
                                        type="email" 
                                        id="email" 
                                        name="email" 
                                        value={formData.email} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: maria.garcia@empresa.com"
                                    />
                                </div>
                            </div>

                            {/* Columna 2 */}
                            <div className={styles.formColumn}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="position" className={styles.formLabel}>
                                        Cargo *
                                    </label>
                                    <input 
                                        type="text" 
                                        id="position" 
                                        name="position" 
                                        value={formData.position} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: Arquitecta Senior"
                                        required 
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="order" className={styles.formLabel}>
                                        Orden de Aparición
                                    </label>
                                    <input 
                                        type="number" 
                                        id="order" 
                                        name="order" 
                                        value={formData.order} 
                                        onChange={handleChange} 
                                        className={styles.formInput}
                                        placeholder="Ej: 1"
                                        min="0"
                                    />
                                    <div className={styles.helperText}>
                                        Los miembros se ordenarán de menor a mayor número
                                    </div>
                                </div>

                                <div className={styles.optionsGroup}>
                                    <label className={styles.formLabel}>Opciones del Miembro</label>
                                    <div className={styles.checkboxCard}>
                                        <label className={styles.checkboxLabel}>
                                            <input 
                                                type="checkbox" 
                                                id="isFeatured" 
                                                name="isFeatured" 
                                                checked={formData.isFeatured} 
                                                onChange={handleChange} 
                                                className={styles.checkboxInput}
                                            />
                                            <span className={styles.checkboxCustom}></span>
                                            <StarIcon className={styles.checkboxIcon} />
                                            <span className={styles.checkboxText}>
                                                <strong>Miembro Destacado</strong>
                                                <small>Aparecerá en secciones especiales</small>
                                            </span>
                                        </label>
                                        
                                        <label className={styles.checkboxLabel}>
                                            <input 
                                                type="checkbox" 
                                                id="isActive" 
                                                name="isActive" 
                                                checked={formData.isActive} 
                                                onChange={handleChange} 
                                                className={styles.checkboxInput}
                                            />
                                            <span className={styles.checkboxCustom}></span>
                                            {formData.isActive ? (
                                                <EyeIcon className={styles.checkboxIcon} />
                                            ) : (
                                                <EyeSlashIcon className={styles.checkboxIcon} />
                                            )}
                                            <span className={styles.checkboxText}>
                                                <strong>Miembro Activo</strong>
                                                <small>Visible en el sitio web</small>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Biografía - Full Width */}
                        <div className={styles.formGroupFull}>
                            <label htmlFor="bio" className={styles.formLabel}>
                                Biografía
                                <span className={styles.charCount}>
                                    {formData.bio?.length || 0}/500 caracteres
                                </span>
                            </label>
                            <textarea 
                                id="bio" 
                                name="bio" 
                                value={formData.bio || ''} 
                                onChange={handleChange} 
                                className={styles.formTextarea}
                                placeholder="Describe la experiencia, especialidades y logros del miembro del equipo..."
                                rows={6}
                                maxLength={500}
                            />
                            <div className={styles.textareaFooter}>
                                <span className={styles.helperText}>
                                    Esta biografía aparecerá en el perfil público del miembro
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Pestaña: Foto de Perfil */}
                {activeTab === 'profile' && (
                    <div className={styles.tabContent}>
                        <div className={styles.profileSection}>
                            <div className={styles.sectionHeader}>
                                <PhotoIcon className={styles.sectionIcon} />
                                <div>
                                    <h3 className={styles.sectionTitle}>Foto de Perfil</h3>
                                    <p className={styles.sectionDescription}>
                                        Sube una imagen profesional para el perfil del miembro
                                    </p>
                                </div>
                            </div>

                            <div className={styles.uploadSection}>
                                {formData.imageUrl ? (
                                    <div className={styles.imagePreviewContainer}>
                                        <div className={styles.imagePreview}>
                                            <img 
                                                src={formData.imageUrl} 
                                                alt={`${formData.firstName} ${formData.lastName}`}
                                                className={styles.previewImage}
                                            />
                                            <div className={styles.imageOverlay}>
                                                <button 
                                                    type="button" 
                                                    className={styles.imageActionButton}
                                                    onClick={() => window.open(formData.imageUrl, '_blank')}
                                                    title="Ver imagen completa"
                                                >
                                                    <EyeIcon className={styles.actionIcon} />
                                                </button>
                                                <button 
                                                    type="button" 
                                                    className={`${styles.imageActionButton} ${styles.deleteButton}`}
                                                    onClick={handleRemoveImage}
                                                    title="Eliminar imagen"
                                                >
                                                    <TrashIcon className={styles.actionIcon} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className={styles.imageInfo}>
                                            <p className={styles.imageInfoText}>
                                                Foto actual de {formData.firstName} {formData.lastName}
                                            </p>
                                            <label htmlFor="profileImage" className={styles.changeImageButton}>
                                                <PlusIcon className={styles.uploadIcon} />
                                                Cambiar Imagen
                                                <input 
                                                    type="file" 
                                                    id="profileImage" 
                                                    name="profileImage" 
                                                    onChange={handleImageUpload} 
                                                    disabled={isUploading}
                                                    accept="image/*"
                                                    className={styles.uploadInput}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.uploadArea}>
                                        <label htmlFor="profileImage" className={styles.uploadLabel}>
                                            <PlusIcon className={styles.uploadIcon} />
                                            <span>Seleccionar Imagen de Perfil</span>
                                            <input 
                                                type="file" 
                                                id="profileImage" 
                                                name="profileImage" 
                                                onChange={handleImageUpload} 
                                                disabled={isUploading}
                                                accept="image/*"
                                                className={styles.uploadInput}
                                            />
                                        </label>
                                        {isUploading && (
                                            <div className={styles.uploadingIndicator}>
                                                <div className={styles.spinner}></div>
                                                Subiendo imagen...
                                            </div>
                                        )}
                                        <div className={styles.uploadTips}>
                                            <h4>Recomendaciones:</h4>
                                            <ul>
                                                <li>Formato: JPG, PNG o WebP</li>
                                                <li>Tamaño recomendado: 400x400 píxeles</li>
                                                <li>Máximo 5MB por imagen</li>
                                                <li>Fondo neutro y iluminación adecuada</li>
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Botones de acción */}
                <div className={styles.formActions}>
                    <button 
                        type="button" 
                        className={styles.cancelButton}
                        onClick={() => navigate('/team')}
                    >
                        Cancelar
                    </button>
                    
                    <div className={styles.actionButtons}>
                        {activeTab !== 'basic' && (
                            <button 
                                type="button" 
                                className={styles.secondaryButton}
                                onClick={() => setActiveTab('basic')}
                            >
                                ← Información Básica
                            </button>
                        )}
                        
                        {activeTab === 'basic' && (
                            <button 
                                type="button" 
                                className={styles.secondaryButton}
                                onClick={() => setActiveTab('profile')}
                            >
                                Siguiente: Foto de Perfil →
                            </button>
                        )}
                        
                        <button 
                            type="submit" 
                            className={styles.primaryButton}
                            disabled={isUploading}
                        >
                            {isUploading ? (
                                <>
                                    <div className={styles.spinner}></div>
                                    Procesando...
                                </>
                            ) : (
                                isEditing ? 'Guardar Cambios' : 'Crear Miembro'
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default TeamForm;